import React, { useState } from 'react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { useToast } from '@/shared/hooks/use-toast';
import { handleApiError } from '@/shared/api/requests';
import {
  RoleFormDialog,
  useProjectPermissions,
  useProjectRoles,
  useRolesMutations,
} from '@/features/roles-and-permissions';
import type { RoleInput } from '@/features/roles-and-permissions';

interface RoleComboboxProps {
  projectId: number | undefined;
  /** Selected ProjectRole id, as a string (combobox values are strings). */
  value: string;
  onChange: (roleId: string) => void;
  /** Whether the current user may create new roles inline. */
  canManageRbac?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Role selector for the project-team invite/add flows.
 *
 * Lists the project's existing `ProjectRole`s and — for users who can manage
 * RBAC — lets them create a new role inline using the same `RoleFormDialog`
 * used on the project's "Roles & Permissions" tab, so permissions can be
 * selected at creation time.
 */
export const RoleCombobox: React.FC<RoleComboboxProps> = ({
  projectId,
  value,
  onChange,
  canManageRbac = false,
  placeholder = 'Select a role',
  disabled = false,
}) => {
  const { toast } = useToast();
  const { data: roles = [], isLoading } = useProjectRoles(projectId);
  const { data: permissions = [] } = useProjectPermissions(projectId);
  const { createRole } = useRolesMutations(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);

  const options = roles.map((role) => ({
    value: String(role.id),
    label: role.name,
  }));

  const handleCreateRole = async (payload: RoleInput) => {
    try {
      const role = await createRole.mutateAsync(payload);
      onChange(String(role.id));
      setDialogOpen(false);
      toast({ title: `Role "${role.name}" created.` });
    } catch (error) {
      toast({
        title: 'Unable to create role',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <CustomCombobox
        options={options}
        value={value}
        onChange={onChange}
        showAddNew={canManageRbac}
        onAddNewClick={() => setDialogOpen(true)}
        addNewLabel="Create role"
        placeholder={isLoading ? 'Loading roles...' : placeholder}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found."
        disabled={disabled || !projectId}
      />

      <RoleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        permissions={permissions}
        role={null}
        isSubmitting={createRole.isPending}
        onSubmit={handleCreateRole}
      />
    </>
  );
};
